import type { TranslationAvailability, TranslatorBackend } from "@/main/translation/TranslatorBackend";

// On-device translation, bridged to the Android shell's `SeavienTranslatePlugin` (SV-140 D1, slice 1b).
//
// Reached through `window.Capacitor.Plugins`, never `import "@capacitor/*"` (A2): the same seam rule as
// `MobileHapticsBridge.ts`, and what keeps this module's bundle-budget cost near zero (rule 27).
//
// NO FEATURE FLAG (rule 35). The fallback chain IS the flag: on the web, and in every APK built before
// the plugin shipped, `window.Capacitor.Plugins.SeavienTranslate` is `undefined`, `hasNativeTranslator()`
// is false, and the reader sees exactly what they see today (no button, A2).
//
// THE CONTRACT, fixed by the work order §4 and implemented verbatim by slice 1b. Neither side may change it:
//
//   SeavienTranslate.availability({source, target}) → {status: "available" | "downloadable" | "unavailable"}
//   SeavienTranslate.isMetered() → {metered: boolean}
//   SeavienTranslate.download({source, target}) → {} | reject
//   SeavienTranslate.translate({source, target, texts: string[]}) → {texts: string[]}   // same length, same order
//
// Every answer is validated here rather than trusted: an unknown status is `unavailable`, a translate
// result of the wrong length or type is a rejection. The plugin never sees UI and never logs text.

type LanguagePair = { source: string; target: string };

type SeavienTranslatePlugin = {
    availability: (pair: LanguagePair) => Promise<{ status?: unknown } | undefined>;
    isMetered: () => Promise<{ metered?: unknown } | undefined>;
    download: (pair: LanguagePair) => Promise<unknown>;
    translate: (request: LanguagePair & { texts: string[] }) => Promise<{ texts?: unknown } | undefined>;
};

type CapacitorPluginsRuntime = {
    SeavienTranslate?: Partial<SeavienTranslatePlugin>;
};

const KNOWN_STATUSES: ReadonlySet<string> = new Set(["available", "downloadable", "unavailable"]);

/**
 * The plugin, only when every one of the four contract methods is present. A half-registered plugin
 * (an older build exposing a subset) is treated as absent: a button that cannot finish is worse than none.
 */
export function getSeavienTranslatePlugin(): SeavienTranslatePlugin | undefined {
    if (typeof window === "undefined") {
        return undefined;
    }
    const plugin = (window as unknown as {
        Capacitor?: { Plugins?: CapacitorPluginsRuntime };
    }).Capacitor?.Plugins?.SeavienTranslate;
    if (
        !plugin
        || typeof plugin.availability !== "function"
        || typeof plugin.isMetered !== "function"
        || typeof plugin.download !== "function"
        || typeof plugin.translate !== "function"
    ) {
        return undefined;
    }
    return plugin as SeavienTranslatePlugin;
}

/** Feature detection only. Cheap and synchronous, so the eager graph can ask it on every page. */
export function hasNativeTranslator(): boolean {
    return getSeavienTranslatePlugin() !== undefined;
}

/** The native backend, or `null` on the web and on an app build without the plugin. */
export function nativeTranslatorBackend(): TranslatorBackend | null {
    const plugin = getSeavienTranslatePlugin();
    if (!plugin) {
        return null;
    }
    return {
        kind: "native",
        async availability(source: string, target: string): Promise<TranslationAvailability> {
            try {
                const result = await plugin.availability({ source, target });
                const status = result?.status;
                return typeof status === "string" && KNOWN_STATUSES.has(status)
                    ? status as TranslationAvailability
                    : "unavailable";
            } catch {
                return "unavailable";
            }
        },
        async isMetered(): Promise<boolean> {
            try {
                const result = await plugin.isMetered();
                return typeof result?.metered === "boolean" ? result.metered : true;
            } catch {
                return true;
            }
        },
        async download(source: string, target: string): Promise<void> {
            await plugin.download({ source, target });
        },
        async translate(source: string, target: string, texts: string[]): Promise<string[]> {
            const result = await plugin.translate({ source, target, texts });
            const translated = result?.texts;
            if (
                !Array.isArray(translated)
                || translated.length !== texts.length
                || translated.some((text) => typeof text !== "string")
            ) {
                throw new Error("SeavienTranslate.translate returned a result that does not match the request");
            }
            return translated as string[];
        },
    };
}
